import Tabs from "@imminently/decisively-ui/Tabs";
import * as React from "react";

import {
  ComponentPage,
  ComponentPreview,
  ComponentSection,
} from "../components/ComponentPage";

const LazyPanel = React.lazy(() =>
  new Promise<{ default: React.ComponentType }>((resolve) => {
    window.setTimeout(() => {
      import("./LazyTabsPanel").then(resolve);
    }, 700);
  }),
);

export function TabsPage() {
  return (
    <ComponentPage
      title="Tabs"
      description="Switch between related panels, with optional lazy-loaded tab content."
    >
      <ComponentSection
        title="Lazy Content"
        description="Tabs.Content can load its panel with a dynamic import and displays a loading spinner while suspended."
      >
        <ComponentPreview className="items-stretch justify-stretch">
          <Tabs defaultValue="overview" className="flex min-h-[220px] w-full flex-col">
            <Tabs.List>
              <Tabs.Trigger value="overview">Overview</Tabs.Trigger>
              <Tabs.Trigger value="lazy">Lazy</Tabs.Trigger>
            </Tabs.List>
            <Tabs.Content value="overview" className="flex-1 p-4">
              <div className="space-y-2 text-sm">
                <div className="font-medium">Eager panel</div>
                <p className="text-muted-foreground">
                  This content is rendered normally as children of Tabs.Content.
                </p>
              </div>
            </Tabs.Content>
            <Tabs.Content
              value="lazy"
              className="flex-1 p-4"
            >
              <LazyPanel />
            </Tabs.Content>
          </Tabs>
        </ComponentPreview>
        <pre className="overflow-auto rounded-md bg-muted p-4 text-xs">
{`<Tabs.Content
  value="load-testing"
>
  <LazyLoadTesting />
</Tabs.Content>`}
        </pre>
      </ComponentSection>
      <ComponentSection
        title="URL Sync"
        description="Tabs can read and write the selected value to a URL query parameter."
      >
        <ComponentPreview className="items-stretch justify-stretch">
          <Tabs defaultValue="details" urlSync="scenarioTab" className="flex min-h-[180px] w-full flex-col">
            <Tabs.List>
              <Tabs.Trigger value="details">Details</Tabs.Trigger>
              <Tabs.Trigger value="history">History</Tabs.Trigger>
            </Tabs.List>
            <Tabs.Content value="details" className="flex-1 p-4">
              <div className="text-sm">Selecting this tab writes <code>scenarioTab=details</code>.</div>
            </Tabs.Content>
            <Tabs.Content value="history" className="flex-1 p-4">
              <div className="text-sm">Selecting this tab writes <code>scenarioTab=history</code>.</div>
            </Tabs.Content>
          </Tabs>
        </ComponentPreview>
        <pre className="overflow-auto rounded-md bg-muted p-4 text-xs">
{`<Tabs defaultValue="scenario" urlSync="scenarioTab">
  ...
</Tabs>`}
        </pre>
      </ComponentSection>
    </ComponentPage>
  );
}
